/**
 * /app/vendor — the vendor console (e.g. Versatile).
 *
 * A fleet-monitoring view of the vendor's equipment slice (things.vendor_org_id
 * = the vendor, under its child foundries): a KPI strip (GET /v1/vendor/fleet/
 * overview), the cross-customer customer table (GET /v1/vendor/customers), and a
 * flat equipment table with firmware + cert health (GET /v1/vendor/equipment) —
 * all RequireVendorAdmin server-side, never the foundries' process data. Gated on
 * useOrg().isVendorAdmin. See docs/versatile/specs/tenancy-vendor-portal.md §4.
 */

import { useMemo, useState, type ReactNode } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { Building2, ChevronRight, Plus } from "lucide-react";
import { PageContainer } from "@/routes/_layout";
import { PageHeader } from "@/lib/ui/page-header";
import { Button } from "@/lib/ui/button";
import { Badge } from "@/lib/ui/badge";
import { Card } from "@/lib/ui/card";
import { cn } from "@/lib/ui/cn";
import { useOrg } from "@/lib/org-context";
import { isLiveApi } from "@/lib/api";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  vendor,
  type VendorAlarm,
  type VendorCustomer,
  type VendorCustomerUsage,
  type VendorEquipment,
  type VendorFleetSummary,
  type VendorTag,
  type VendorUsageSeries,
} from "@/lib/api/vendor";

export function VendorDashboard() {
  const { isVendorAdmin } = useOrg();
  const navigate = useNavigate();
  const enabled = isLiveApi && isVendorAdmin;
  const customersQuery = useQuery({
    queryKey: ["vendor", "customers"],
    queryFn: () => vendor.listCustomers(),
    enabled,
    staleTime: 30_000,
  });
  const fleetQuery = useQuery({
    queryKey: ["vendor", "fleet"],
    queryFn: () => vendor.fleetOverview(),
    enabled,
    staleTime: 30_000,
  });
  const equipmentQuery = useQuery({
    queryKey: ["vendor", "equipment"],
    queryFn: () => vendor.listEquipment(),
    enabled,
    staleTime: 30_000,
  });
  const usageQuery = useQuery({
    queryKey: ["vendor", "usage"],
    queryFn: () => vendor.usageIngestRate("1d"),
    enabled,
    staleTime: 60_000,
  });
  const alarmsQuery = useQuery({
    queryKey: ["vendor", "alarms"],
    queryFn: () => vendor.fleetAlarms(),
    enabled,
    staleTime: 30_000,
  });
  const byCustomerQuery = useQuery({
    queryKey: ["vendor", "usage-by-customer"],
    queryFn: () => vendor.usageByCustomer("1d"),
    enabled,
    staleTime: 60_000,
  });

  // Self-guard (defence in depth; the nav + route are already vendor-gated).
  if (!isVendorAdmin) return <Navigate to="/app" replace />;

  const customers = customersQuery.data?.items ?? [];
  const equipment = equipmentQuery.data?.items ?? [];
  const alarms = alarmsQuery.data?.items ?? [];
  const byCustomer = byCustomerQuery.data?.items ?? [];

  return (
    <PageContainer className="max-w-6xl">
      <PageHeader
        breadcrumb={
          <>
            <Link to="/app" className="hover:text-on-surface">
              App
            </Link>
            <span aria-hidden>/</span>
            <span className="text-on-surface">Fleet</span>
          </>
        }
        title="Fleet"
        description="The foundries you've onboarded and the equipment you manufacture across them — live status, firmware and certificate health."
        actions={
          <Button size="sm" onClick={() => navigate("/app/vendor/onboard")}>
            <Plus className="mr-1.5 size-4" />
            Add customer
          </Button>
        }
      />

      {!isLiveApi ? (
        <Note>
          The vendor console needs the live API — it reads
          <span className="font-mono"> /v1/vendor/*</span>.
        </Note>
      ) : (
        <div className="space-y-8">
          {fleetQuery.data && <FleetStrip s={fleetQuery.data} />}

          <Section title="Customers">
            {customersQuery.isLoading ? (
              <Note>Loading customers…</Note>
            ) : customersQuery.isError ? (
              <Note>
                Couldn't load customers.{" "}
                <button type="button" className="underline" onClick={() => customersQuery.refetch()}>
                  Retry
                </button>
              </Note>
            ) : customers.length === 0 ? (
              <EmptyCustomers onAdd={() => navigate("/app/vendor/onboard")} />
            ) : (
              <CustomerTable customers={customers} />
            )}
          </Section>

          <Section title="Alarms">
            {alarmsQuery.isLoading ? (
              <Note>Loading alarms…</Note>
            ) : alarms.length === 0 ? (
              <Note>No active alarms across your fleet.</Note>
            ) : (
              <AlarmsList items={alarms} />
            )}
          </Section>

          {usageQuery.data && usageQuery.data.points.length > 0 && (
            <Section title="Usage">
              <UsageSection series={usageQuery.data} />
            </Section>
          )}

          {byCustomer.length > 0 && (
            <Section title="Usage by customer">
              <UsageByCustomer items={byCustomer} />
            </Section>
          )}

          {equipment.length > 0 && (
            <Section title="Equipment">
              <EquipmentTable items={equipment} />
            </Section>
          )}
        </div>
      )}
    </PageContainer>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold text-on-surface">{title}</h2>
      {children}
    </section>
  );
}

function FleetStrip({ s }: { s: VendorFleetSummary }) {
  const tiles: { label: string; value: string; danger?: boolean }[] = [
    { label: "Equipment online", value: `${s.online} / ${s.total_equipment}` },
    { label: "Gateways online", value: `${s.gateways_online} / ${s.gateways}` },
    { label: "Instruments online", value: `${s.instruments_online} / ${s.instruments}` },
    { label: "Active customers", value: `${s.customers_active} / ${s.customers}` },
    { label: "Certs expiring (30d)", value: `${s.certs_expiring_30d}`, danger: s.certs_expiring_30d > 0 },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      {tiles.map((t) => (
        <Card key={t.label} className="p-4">
          <div className="text-[11px] uppercase tracking-wide text-on-surface-muted">{t.label}</div>
          <div
            className={cn(
              "mt-1 text-2xl font-semibold tabular-nums",
              t.danger ? "text-danger" : "text-on-surface",
            )}
          >
            {t.value}
          </div>
        </Card>
      ))}
    </div>
  );
}

function Note({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-surface-elevated/40 p-10 text-center text-sm text-on-surface-muted">
      {children}
    </div>
  );
}

function EmptyCustomers({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-surface-elevated/40 p-12 text-center">
      <Building2 className="mx-auto size-8 text-on-surface-muted" strokeWidth={1.2} />
      <p className="mt-3 text-sm font-medium text-on-surface">No customers yet</p>
      <p className="mx-auto mt-1 max-w-md text-xs text-on-surface-muted">
        Onboard your first foundry — they get a branded login and you keep oversight of
        their equipment.
      </p>
      <Button size="sm" className="mt-4" onClick={onAdd}>
        <Plus className="mr-1.5 size-4" />
        Add customer
      </Button>
    </div>
  );
}

function CustomerTable({ customers }: { customers: VendorCustomer[] }) {
  const navigate = useNavigate();
  return (
    <Card className="overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-surface-muted/40 text-left text-[11px] uppercase tracking-wide text-on-surface-muted">
            <th className="px-4 py-2.5 font-medium">Foundry</th>
            <th className="px-4 py-2.5 font-medium">Equipment</th>
            <th className="px-4 py-2.5 font-medium">Online</th>
            <th className="px-4 py-2.5 font-medium">Onboarded</th>
            <th className="px-4 py-2.5 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {customers.map((c) => (
            <tr
              key={c.id}
              onClick={() => navigate(`/app/vendor/customers/${c.id}`)}
              className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-surface-muted/30"
            >
              <td className="px-4 py-3">
                <div className="font-medium text-on-surface">{c.name}</div>
                <div className="font-mono text-[11px] text-on-surface-muted">{c.slug}</div>
              </td>
              <td className="px-4 py-3 tabular-nums text-on-surface">{c.equipment_count}</td>
              <td className="px-4 py-3">
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className={cn(
                      "size-1.5 rounded-full",
                      c.online_count > 0 ? "bg-success" : "bg-on-surface-muted/40",
                    )}
                  />
                  <span className="tabular-nums text-on-surface">{c.online_count}</span>
                  <span className="text-on-surface-muted">/ {c.equipment_count}</span>
                </span>
              </td>
              <td className="px-4 py-3 text-on-surface-muted">{relative(c.created_at)}</td>
              <td className="px-4 py-3">
                {c.is_disabled ? (
                  <Badge variant="secondary">Disabled</Badge>
                ) : (
                  <Badge variant="success">Active</Badge>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

export function EquipmentTable({ items }: { items: VendorEquipment[] }) {
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-surface-muted/40 text-left text-[11px] uppercase tracking-wide text-on-surface-muted">
              <th className="px-4 py-2.5 font-medium">Equipment</th>
              <th className="px-4 py-2.5 font-medium">Customer</th>
              <th className="px-4 py-2.5 font-medium">Type</th>
              <th className="px-4 py-2.5 font-medium">Identity</th>
              <th className="px-4 py-2.5 font-medium">Hardware</th>
              <th className="px-4 py-2.5 font-medium">Cert expiry</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <EquipmentRow key={e.id} e={e} />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** One equipment row — click to expand the device's tags + latest readings (the
 *  equipment-scoped drill-in, fetched on first expand via the slice-guarded
 *  /customers/{org}/equipment/{thing}/tags endpoint). */
function EquipmentRow({ e }: { e: VendorEquipment }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr
        onClick={() => setOpen((o) => !o)}
        className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-surface-muted/30"
      >
        <td className="px-4 py-3">
          <div className="flex items-center gap-1.5">
            <ChevronRight
              className={cn(
                "size-3.5 shrink-0 text-on-surface-muted transition-transform",
                open && "rotate-90",
              )}
            />
            <div>
              <div className="font-medium text-on-surface">{e.name}</div>
              <div className="font-mono text-[11px] text-on-surface-muted">{e.slug}</div>
            </div>
          </div>
        </td>
        <td className="px-4 py-3 text-on-surface-muted">{e.org_name}</td>
        <td className="px-4 py-3">
          {e.kind === "gateway" ? (
            <Badge variant="secondary">Gateway</Badge>
          ) : (
            <Badge variant="outline">Instrument</Badge>
          )}
        </td>
        <td className="px-4 py-3">{identityCell(e)}</td>
        <td className="px-4 py-3 font-mono text-[11px] text-on-surface-muted">
          {e.hardware_rev ?? "—"}
        </td>
        <td className="px-4 py-3">{certCell(e.cert_expires_at)}</td>
        <td className="px-4 py-3">
          <span className="inline-flex items-center gap-1.5">
            <span
              className={cn(
                "size-1.5 rounded-full",
                e.online ? "bg-success" : "bg-on-surface-muted/40",
              )}
            />
            <span className="text-on-surface-muted">
              {e.last_seen ? relative(e.last_seen) : "never"}
            </span>
          </span>
        </td>
      </tr>
      {open && (
        <tr className="border-b border-border/60 bg-surface-muted/20">
          <td colSpan={7} className="px-4 py-3">
            <EquipmentReadings orgId={e.org_id} thingId={e.id} />
          </td>
        </tr>
      )}
    </>
  );
}

/** The expanded drill-in: the device's tags (latest value) as a selectable grid,
 *  and a per-tag trend chart below. Only mounted while the row is open, so the
 *  tags query runs on first expand. Honours the foundry's data-hidden flag. */
function EquipmentReadings({ orgId, thingId }: { orgId: string; thingId: string }) {
  const [selected, setSelected] = useState<VendorTag | null>(null);
  const [range, setRange] = useState<TrendRange>("7d");
  const tagsQuery = useQuery({
    queryKey: ["vendor", "equipment-tags", orgId, thingId],
    queryFn: () => vendor.equipmentTags(orgId, thingId),
    staleTime: 30_000,
  });

  if (tagsQuery.isLoading) {
    return <span className="text-xs text-on-surface-muted">Loading readings…</span>;
  }
  if (tagsQuery.data?.data_hidden) {
    return (
      <span className="text-xs text-on-surface-muted">
        This customer has hidden its instrument data from you.
      </span>
    );
  }
  const tags = tagsQuery.data?.items ?? [];
  if (tags.length === 0) {
    return <span className="text-xs text-on-surface-muted">No tags on this device.</span>;
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-x-8 gap-y-1 sm:grid-cols-3">
        {tags.map((t) => {
          const active = selected?.id === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setSelected(active ? null : t)}
              className={cn(
                "flex items-baseline justify-between gap-3 rounded px-1.5 py-1 text-left transition-colors hover:bg-surface-muted/50",
                active && "bg-surface-muted",
              )}
            >
              <span className="text-xs text-on-surface">{t.name}</span>
              <span className="text-xs tabular-nums text-on-surface-muted">
                {t.latest_value ?? "—"}
                {t.unit ? ` ${t.unit}` : ""}
              </span>
            </button>
          );
        })}
      </div>
      {selected && (
        <TagTrend
          orgId={orgId}
          thingId={thingId}
          tag={selected}
          range={range}
          onRange={setRange}
        />
      )}
    </div>
  );
}

type TrendRange = "1h" | "6h" | "24h" | "7d" | "30d";

const RANGE_MS: Record<TrendRange, number> = {
  "1h": 3_600_000,
  "6h": 6 * 3_600_000,
  "24h": 24 * 3_600_000,
  "7d": 7 * 86_400_000,
  "30d": 30 * 86_400_000,
};

/** Per-tag trend — fetches the tag's value history over the selected window and
 *  draws a small line chart (x = time, y = value, in the tag's unit). Honours the
 *  foundry's data-hidden flag independently of the tags call. */
function TagTrend({
  orgId,
  thingId,
  tag,
  range,
  onRange,
}: {
  orgId: string;
  thingId: string;
  tag: VendorTag;
  range: TrendRange;
  onRange: (r: TrendRange) => void;
}) {
  // The window is derived at fetch time (inside queryFn, not during render, so
  // `Date.now` stays out of the render path) and keyed only by `range`, so it
  // moves when the range changes or the query refetches.
  const valuesQuery = useQuery({
    queryKey: ["vendor", "equipment-tag-values", orgId, thingId, tag.id, range],
    queryFn: () => {
      const now = Date.now();
      return vendor.equipmentTagValues(orgId, thingId, tag.id, {
        from: new Date(now - RANGE_MS[range]).toISOString(),
        to: new Date(now).toISOString(),
      });
    },
    staleTime: 30_000,
  });

  const unit = valuesQuery.data?.unit || tag.unit;
  const points = useMemo(
    () => (valuesQuery.data?.points ?? []).map((p) => ({ t: new Date(p.t).getTime(), v: p.v })),
    [valuesQuery.data],
  );
  // A day or less reads the time of day; a longer window the date.
  const timeFmt = (v: number) =>
    RANGE_MS[range] <= RANGE_MS["24h"]
      ? new Date(v).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : new Date(v).toLocaleDateString([], { month: "short", day: "numeric" });

  return (
    <div className="rounded-md border border-border bg-surface p-3">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="text-xs font-medium text-on-surface">
          {tag.name}
          {unit ? <span className="text-on-surface-muted"> ({unit})</span> : null}
        </div>
        <div className="flex items-center gap-1">
          {(Object.keys(RANGE_MS) as TrendRange[]).map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => onRange(r)}
              className={cn(
                "rounded px-1.5 py-0.5 text-[11px] tabular-nums transition-colors",
                r === range
                  ? "bg-brand/15 text-brand"
                  : "text-on-surface-muted hover:bg-surface-muted",
              )}
            >
              {r}
            </button>
          ))}
        </div>
      </div>
      {valuesQuery.isLoading ? (
        <TrendMessage>Loading trend…</TrendMessage>
      ) : valuesQuery.data?.data_hidden ? (
        <TrendMessage>This customer has hidden its instrument data from you.</TrendMessage>
      ) : points.length === 0 ? (
        <TrendMessage>No readings in this window.</TrendMessage>
      ) : (
        <div className="h-40 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="rgb(var(--color-border))" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="t"
                type="number"
                domain={["dataMin", "dataMax"]}
                scale="time"
                tickFormatter={timeFmt}
                stroke="rgb(var(--color-on-surface-muted))"
                tick={{ fontSize: 10 }}
                tickMargin={6}
                minTickGap={32}
              />
              <YAxis
                stroke="rgb(var(--color-on-surface-muted))"
                tick={{ fontSize: 10 }}
                width={40}
                domain={["auto", "auto"]}
              />
              <Tooltip
                labelFormatter={(v) => new Date(Number(v)).toLocaleString()}
                formatter={(v: number) => [`${v}${unit ? ` ${unit}` : ""}`, tag.name]}
                contentStyle={{
                  backgroundColor: "rgb(var(--color-surface-elevated))",
                  border: "1px solid rgb(var(--color-border))",
                  borderRadius: 6,
                  fontSize: 12,
                }}
              />
              <Line
                type="monotone"
                dataKey="v"
                stroke="rgb(var(--color-brand))"
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function TrendMessage({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-40 items-center justify-center text-xs text-on-surface-muted">
      {children}
    </div>
  );
}

/** Device identity — the MAC for a legacy V-Sync (the authoritative id), or the
 *  mTLS-cert marker for a native unit, else "—". */
function identityCell(e: VendorEquipment): ReactNode {
  if (e.mac) return <span className="font-mono text-[11px] text-on-surface">{e.mac}</span>;
  if (e.cert_expires_at) return <span className="text-[11px] text-on-surface-muted">mTLS cert</span>;
  return <span className="text-on-surface-muted">—</span>;
}

/** Cert-expiry cell — "—" when none; red when it expires within 30 days. */
function certCell(iso: string | null): ReactNode {
  if (!iso) return <span className="text-on-surface-muted">—</span>;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return <span className="text-on-surface-muted">—</span>;
  const soon = d.getTime() - Date.now() < 30 * 86_400_000;
  return <span className={cn("tabular-nums", soon ? "text-danger" : "text-on-surface-muted")}>{relative(iso)}</span>;
}

function UsageSection({ series }: { series: VendorUsageSeries }) {
  const total = series.points.reduce((a, p) => a + p.readings, 0);
  const data = series.points.map((p) => ({ t: new Date(p.bucket).getTime(), readings: p.readings }));
  return (
    <Card className="p-4">
      <div className="mb-2 flex items-baseline justify-between">
        <div className="text-2xl font-semibold tabular-nums text-on-surface">
          {total.toLocaleString()}
        </div>
        <div className="text-xs text-on-surface-muted">readings · last 24h</div>
      </div>
      <div className="h-32 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <XAxis dataKey="t" type="number" domain={["dataMin", "dataMax"]} hide />
            <YAxis hide />
            <Tooltip
              labelFormatter={(v) => new Date(Number(v)).toLocaleString()}
              formatter={(v: number) => [v.toLocaleString(), "readings"]}
              contentStyle={{
                backgroundColor: "rgb(var(--color-surface-elevated))",
                border: "1px solid rgb(var(--color-border))",
                borderRadius: 6,
                fontSize: 12,
              }}
            />
            <Area
              type="monotone"
              dataKey="readings"
              stroke="rgb(var(--color-brand))"
              fill="rgb(var(--color-brand))"
              fillOpacity={0.15}
              strokeWidth={1.5}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function UsageByCustomer({ items }: { items: VendorCustomerUsage[] }) {
  const max = Math.max(1, ...items.map((i) => i.readings));
  return (
    <Card className="space-y-2.5 p-4">
      {items.map((c) => (
        <div key={c.org_id} className="space-y-1">
          <div className="flex items-baseline justify-between text-xs">
            <span className="text-on-surface">{c.org_name}</span>
            <span className="tabular-nums text-on-surface-muted">
              {c.readings.toLocaleString()} · {c.devices} dev
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded bg-surface-muted/40">
            <div
              className="h-full rounded"
              style={{ width: `${(c.readings / max) * 100}%`, backgroundColor: "rgb(var(--color-brand))" }}
            />
          </div>
        </div>
      ))}
    </Card>
  );
}

function sevBadge(sev: string): ReactNode {
  if (sev === "critical")
    return (
      <Badge variant="outline" className="border-transparent bg-danger/15 text-danger">
        {sev}
      </Badge>
    );
  if (sev === "warning") return <Badge variant="secondary">{sev}</Badge>;
  return <Badge variant="outline">{sev}</Badge>;
}

function AlarmsList({ items }: { items: VendorAlarm[] }) {
  return (
    <Card className="overflow-hidden">
      <ul className="divide-y divide-border/60">
        {items.map((a) => (
          <li key={a.id} className="flex items-center gap-3 px-4 py-2.5">
            {sevBadge(a.severity)}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-on-surface">{a.thing_name}</div>
              <div className="truncate text-[11px] text-on-surface-muted">{a.org_name}</div>
            </div>
            <div className="shrink-0 text-[11px] text-on-surface-muted">{relative(a.fired_at)}</div>
            <Badge
              variant="outline"
              className={cn(a.state === "firing" && "border-transparent bg-danger/15 text-danger")}
            >
              {a.state}
            </Badge>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function relative(iso: string): string {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}
